<template>
  <div class="showcase-collection-view">
    <align flex collapse-end direction="flex-start">
      <shared-filters
        show-recent-searches
        :filters.sync="filters"
        :query.sync="query"
        :properties="defaultFilters"
        @clear="clearFilters"
      />

      <direction-sort-options
        :sort.sync="sort"
        :direction.sync="direction"
        :options="sortOptions"
      />
    </align>

    <filter-results
      :is-filtering="isFiltering"
      :filters="filters"
      :results="numResults"
      @clear="clearFilters"
    />

    <div class="results">
      <empty-state
        v-if="showEmptyState"
        icon="search_off"
        title="There are no results for your query"
        description="Try searching for something else"
      />

      <template v-if="loaded">
        <div class="grid">
          <showcase-card
            v-for="card in cards"
            :key="card.id"
            as="router-link"
            :card="card"
            :to="{ query: { ...$route.query, showcaseCardId: card.id } }"
          />
        </div>

        <infinite-scroll
          :loading="loading"
          :has-more-data="hasMoreData"
          @load="loadMore"
        />
      </template>
    </div>

    <scroll-top />
  </div>
</template>

<script>
import debounce from 'lodash.debounce';
import clone from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';

import { SHOWCASE_SORT_OPTIONS, DEFAULT_SHOWCASE_FILTERS } from '@cardladder/shared/constants';

import DirectionSortOptions from '@cardladder/shared/components/DirectionSortOptions';
import ShowcaseCard from '@cardladder/shared/components/ShowcaseCard';

import SharedFilters from '@/components/SharedFilters';
import FilterResults from '@/components/FilterResults';
import { searchService } from '@/services';

export default {
  components: {
    ShowcaseCard,
    SharedFilters,
    FilterResults,
    DirectionSortOptions,
  },

  props: {
    collectionId: String,
  },

  data() {
    return {
      limit: 20,
      cards: [],
      loading: false,
      loaded: false,
      hasMoreData: true,
      page: 1,
      numResults: 0,
      sortOptions: SHOWCASE_SORT_OPTIONS,
      defaultFilters: DEFAULT_SHOWCASE_FILTERS,
      collection: null,
      reset_debounce: null,
    };
  },

  created() {
    this.reset_debounce = debounce(this.reset, 500);
    this.reset();
  },

  mounted() {
    this.load();
  },

  watch: {
    sort() {
      this.reset();
    },

    direction() {
      this.reset();
    },

    filters: {
      handler() {
        this.reset();
      },
      deep: true,
    },

    query() {
      this.reset_debounce();
    },

    collectionId() {
      this.reset();
    },

    page(value) {
      if (value > 1) {
        this.search();
      }
    },
  },

  computed: {
    sort: {
      get() {
        const activeSort = this.sortOptions.find((value) => value === this.$store.state.showcaseSort);

        if (activeSort) {
          return this.$store.state.showcaseSort;
        }

        return this.sortOptions[0];
      },

      set(value) {
        this.$store.commit('SET_SHOWCASE_SORT', value);
      },
    },

    direction: {
      get() {
        return this.$store.state.showcaseDirection;
      },

      set(value) {
        this.$store.commit('SET_SHOWCASE_DIRECTION', value);
      },
    },

    filters: {
      get() {
        return this.$store.state.showcaseFilterMap;
      },

      set(value) {
        this.$store.commit('SET_SHOWCASE_FILTERS', value);
      },
    },

    query: {
      get() {
        return this.$store.state.showcaseQuery;
      },

      set(value) {
        this.$store.commit('SET_SHOWCASE_QUERY', value);
      },
    },

    showEmptyState() {
      return !this.loading && this.loaded && this.cards.length === 0;
    },

    hasFilters() {
      return !isEqual(this.filters, this.defaultFilters);
    },

    isFiltering() {
      return Boolean(this.query?.trim().length > 0) || this.hasFilters;
    },
  },

  methods: {
    async load() {
      try {
        this.collection = await searchService.getDoc('collections', this.collectionId);
      } catch (e) {
        this.$router.replace('/showcase');
      }
    },

    reset() {
      this.page = 1;
      this.hasMoreData = true;
      this.numResults = 0;
      this.loaded = false;

      this.search(true);
    },

    loadMore() {
      this.page += 1;
    },

    async search(reset = false) {
      try {
        this.loading = true;

        const data = await searchService.search({
          index: 'collectioncards',
          query: this.query,
          page: this.page,
          limit: this.limit,
          sort: this.sort,
          direction: this.direction,
          filters: {
            ...this.filters,
            collectionId: this.collectionId,
            public: true,
            hasQuantityAvailable: true,
          },
        });

        const cards = data.hits;

        this.numResults = data.totalHits;

        if (reset) {
          this.cards = cards;
        } else {
          this.cards = [...this.cards, ...cards];
        }

        if (cards.length < this.limit) {
          this.hasMoreData = false;
        }
      } catch (e) {
        // swallow
      }

      this.loaded = true;
      this.loading = false;
    },

    clearFilters() {
      this.filters = clone(this.defaultFilters);
    },
  },
};
</script>

<style lang="scss" scoped>
.results {
  position: relative;
  flex: 1;
  margin-top: $gp;
}

.list {
  display: flex;
  flex-direction: column;

  @media($medium-down) {
    margin: 0 (-$gp);
  }
}

.grid {
  @include grid-panel;
}

.back-link {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  color: $color-gray-5;

  &:hover span {
    text-decoration: underline;
  }
}

.header {
  margin-bottom: $gp;

  h2 {
    margin-bottom: $gp * .25;
  }
}

.badges {
  margin-top: $gp * .5;
}

.showcase-owner-item {
  max-width: 400px;
}
</style>
